// The 📨 box on the order panel (customer-contact plan, phase 3b, 2026-10-15): the customer (or Nora Mikken's store
// manager) being asked for a phone number / a name the carrier refused — every mail with its time and whether it is in
// the mailbox's Sent, the next mail, the Gmail thread, and the two buttons a person needs: Send now (managers) and
// Stop asking (a person takes over). Replies (phase 3c, 16.10): what came back, and — when a person must read it —
// Use +… / Use a number… to put the customer's number on the label. With no live case, the dry run's line, and
// Ask the customer now (managers) where only the automatic rules hold it back (an old refusal — 15.10).
// backend/customer_cases.py, docs/CUSTOMER-MAILS.md.
import { useCallback, useEffect, useState } from 'react';
import { API, authFetch } from './api';
import { fmtWh } from './time';

const CLOSE: Record<string, string> = {
  label_bought: 'a label was bought', fixed: 'the value was corrected', left_tab: 'the order is no longer waiting to be shipped',
  order_gone: 'the order is deleted or archived', stopped: 'a person stopped it',
  test_ended: 'the test mode ended', resolved_in_review: 'marked Done in Review',
  taken_over: 'a person answered in the Gmail thread',
};

export default function CustomerCaseBox({ orderId }: { orderId: string }) {
  const [d, setD] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => {
    authFetch(`${API}/customer-mails/case/${encodeURIComponent(orderId)}`)
      .then(r => (r.ok ? r.json() : null)).then(setD).catch(() => setD(null));
  }, [orderId]);
  useEffect(() => { load(); }, [load]);
  if (!d || (!d.cases?.length && !d.plan)) return null;

  const act = async (path: string, body?: any, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(path); setMsg('');
    const r = await authFetch(`${API}/customer-mails${path}`, { method: 'POST', body: JSON.stringify(body || {}) });
    const j = await r.json().catch(() => ({}));
    setBusy('');
    if (!r.ok) { setMsg(j.detail || `HTTP ${r.status}`); return; }
    setMsg(j.result === 'sent' ? '✓ Sent.' : j.result ? `✓ ${j.result}${j.at ? ` — goes ${fmtWh(j.at, 'short')}` : ''}` : '✓ Done.');
    load();
  };

  const c = d.cases?.[0];
  const p = d.plan;
  const planLine = p && (
      <div className="flex-none px-6 py-2 border-b border-gray-800 bg-[#14161f] text-xs text-gray-400">
        📨 {p.audience === 'internal' ? 'The store manager would be mailed' : `The customer would be asked for ${p.kind === 'phone_refused' ? 'the phone number' : 'the name'}${p.situation_name ? ` (text: order ${p.situation_name})` : ''}`}
        {p.blockers.length ? <> — not yet: {p.blockers.join('; ')}</> : <> at {fmtWh(p.would_send_at, 'short')} ({p.sender} → {(p.to || []).join(', ')})</>}
        {d.can_ask && (
          <button onClick={() => act(`/case/${encodeURIComponent(orderId)}/ask`, {},
            `Ask ${p.audience === 'internal' ? 'the store manager' : (p.to || []).join(', ')} now from ${p.sender}? Reminders follow on the next two days.`
            + (d.test_to && p.audience !== 'internal' ? `

TEST MODE: every mail of this case goes to ${d.test_to}, never to the customer.` : ''))}
            disabled={!!busy} className="ml-3 px-2.5 py-1 bg-sky-700 hover:bg-sky-600 disabled:opacity-40 text-white text-xs rounded">
            {busy ? '…' : p.audience === 'internal' ? 'Mail the store manager now' : 'Ask the customer now'}</button>)}
        {msg && <div className={`mt-1 ${msg.startsWith('✓') ? 'text-green-300' : 'text-red-300'}`}>{msg}</div>}
      </div>
  );
  if (!c) return planLine;
  const live = c.status !== 'closed';
  const what = c.kind === 'phone_refused' ? 'the phone number' : 'the name';
  const head = c.audience === 'internal' ? `📨 The store manager is asked to fix ${what}` : `📨 Asking the customer for ${what}`;
  const tone = !live ? 'bg-[#14161f] border-gray-800 text-gray-400'
    : c.status === 'needs_person' ? 'bg-red-950/30 border-red-900/50 text-red-200' : 'bg-sky-950/30 border-sky-900/50 text-sky-100';
  return (<>
    <div className={`flex-none px-6 py-2.5 border-b text-sm ${tone}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-semibold">{head}</span>
        {c.test && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-700 text-white font-bold" title={`Test case: the mails go to ${c.test_to}, never to the customer`}>TEST → {c.test_to}</span>}
        <span className="text-xs opacity-80">
          {c.status === 'open' && c.next_action === 'send' && c.next_action_at ? `next mail ${fmtWh(c.next_action_at, 'short')}`
            : c.status === 'open' && c.next_action === 'day3' ? `no answer → Review on ${fmtWh(c.next_action_at, 'short')}`
            : c.status === 'needs_person' ? `needs a person: ${c.person_reason || 'see Review'}`
            : c.status === 'closed' ? `closed — ${CLOSE[c.close_reason] || c.close_reason}` : c.status}
        </span>
        <span className="flex-1" />
        {c.gmail_link && <a href={c.gmail_link} target="_blank" rel="noreferrer" className="text-xs underline opacity-90">Open in Gmail</a>}
        {live && c.status === 'open' && c.next_action === 'send' && d.can_send && (
          <button onClick={() => act(`/case/${c.id}/send-now`, { step: c.step })} disabled={!!busy}
            className="px-2.5 py-1 bg-sky-700 hover:bg-sky-600 disabled:opacity-40 text-white text-xs rounded">{busy ? '…' : 'Send now'}</button>)}
        {live && d.can_stop && (
          <button onClick={() => { const note = window.prompt('Stop asking — why? (e.g. "called the customer")'); if (note !== null) act(`/case/${c.id}/stop`, { note }); }}
            disabled={!!busy} className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-xs rounded">Stop asking</button>)}
      </div>
      <div className="mt-1 text-xs space-y-0.5 opacity-90">
        {c.events.filter((e: any) => e.kind !== 'opened').map((e: any, i: number) => (
          <div key={i}>
            {fmtWh(e.at, 'short')} ·{' '}
            {e.kind === 'sent' ? <>mail {(e.detail?.step ?? 0) + 1}{e.detail?.situation ? ` (order ${e.detail.situation})` : ''} from {e.detail?.from} to {(e.detail?.to || []).join(', ')}{e.detail?.in_sent ? ' ✓ in Sent' : ''}</>
              : e.kind === 'needs_person' ? <>needs a person: {e.detail?.why}</>
              : e.kind === 'reply' || e.kind === 'other' ? <>{e.kind === 'reply' ? 'reply' : 'a mail'} from {e.detail?.from}: «{e.detail?.snippet}»{e.detail?.candidates?.length ? ` — numbers: ${e.detail.candidates.join(', ')}` : ''}</>
              : e.kind === 'auto_reply' ? <>automatic reply from {e.detail?.from} (ignored)</>
              : e.kind === 'bounce' ? <>the mail bounced ({e.detail?.from})</>
              : e.kind === 'human_reply' ? <>a person answered in Gmail</>
              : e.kind === 'applied' ? <>{e.detail?.value} used for the label{e.detail?.by && e.detail.by !== 'Claude' ? ` (by ${e.detail.by})` : ''}</>
              : e.kind === 'extracted' ? <>(test) the reply gives {e.detail?.value} — the order was not changed</>
              : e.kind === 'closed' ? <>closed: {CLOSE[e.detail?.reason] || e.detail?.reason}{e.detail?.note ? ` (${e.detail.note})` : ''}</>
              : e.kind}
          </div>
        ))}
      </div>
      {live && c.status === 'needs_person' && c.kind === 'phone_refused' && c.audience === 'customer' && !c.test && d.can_stop && (() => {
        const last = [...c.events].reverse().find((e: any) => e.kind === 'reply' || e.kind === 'other');
        const nums: string[] = last?.detail?.candidates || [];
        return (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs">
            {nums.map(n => (
              <button key={n} onClick={() => act(`/case/${c.id}/use-number`, { phone: n }, `Use ${n} for the label and buy it?`)} disabled={!!busy}
                className="px-2.5 py-1 bg-green-700 hover:bg-green-600 disabled:opacity-40 text-white rounded">Use {n}</button>
            ))}
            <button onClick={() => { const n = window.prompt('The phone number from the reply:'); if (n) act(`/case/${c.id}/use-number`, { phone: n }); }}
              disabled={!!busy} className="px-2.5 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded">Use a number…</button>
          </div>
        );
      })()}
      {msg && !planLine && <div className={`text-xs mt-1 ${msg.startsWith('✓') ? 'text-green-300' : 'text-red-300'}`}>{msg}</div>}
    </div>
    {!live && planLine}
  </>);
}
